import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePlan } from '../../contexts/PlanContext';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import UpgradePrompt from '../../components/UpgradePrompt';
import { useToast } from '../../components/ui/use-toast';
import { Trash2, Mail, UserPlus, X } from 'lucide-react';
import { Badge } from '../../components/ui/badge';

interface TeamMember {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  position?: string;
  is_customer_admin: boolean;
  created_at: string;
}

interface Invitation {
  id: number;
  email: string;
  expires_at: string;
  inviter: { first_name: string; last_name: string };
}

const Team = () => {
  const { t } = useTranslation('billing');
  const { canUse, isAtLimit } = usePlan();
  const { toast } = useToast();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [emails, setEmails] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviting, setInviting] = useState(false);

  const enabled = canUse('team_invites');

  const fetchData = async () => {
    try {
      setLoading(true);
      const [membersRes, invitationsRes] = await Promise.all([
        api.get<any>('/team/members'),
        api.get<any>('/team/invitations'),
      ]);
      setMembers((membersRes as any).members || (membersRes as any).data?.members || []);
      setInvitations((invitationsRes as any).invitations || (invitationsRes as any).data?.invitations || []);
    } catch (err) {
      console.error('Failed to fetch team data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (enabled) fetchData();
  }, [enabled]);

  const MAX_TEAM_SIZE = 10;
  const remainingSlots = Math.max(0, MAX_TEAM_SIZE - members.length - invitations.length);
  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const atEmailLimit = emails.length >= remainingSlots;

  const addEmail = (raw: string) => {
    const trimmed = raw.trim().replace(/,+$/, '').trim();
    if (!trimmed) return;
    if (!isValidEmail(trimmed)) {
      toast({ title: t('invalid_email') || 'Invalid email address', variant: 'destructive' });
      return;
    }
    if (emails.length >= remainingSlots) return;
    if (emails.includes(trimmed)) return;
    setEmails((prev) => [...prev, trimmed]);
    setInputValue('');
  };

  const removeEmail = (target: string) => {
    setEmails((prev) => prev.filter((e) => e !== target));
  };

  const handleInvite = async () => {
    if (emails.length === 0) return;
    try {
      setInviting(true);
      const results = await Promise.allSettled(
        emails.map((e) => api.post('/team/invite', { email: e }))
      );
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length > 0) {
        toast({ title: `${failed.length} invitation(s) failed`, variant: 'destructive' });
      } else {
        toast({ title: t('team_invitation_sent') || 'Invitation(s) sent!' });
      }
      setEmails([]);
      setInputValue('');
      fetchData();
    } catch (err: any) {
      toast({ title: err.message || 'Error', variant: 'destructive' });
    } finally {
      setInviting(false);
    }
  };

  const handleRevoke = async (id: number) => {
    try {
      await api.delete(`/team/invitations/${id}`);
      fetchData();
    } catch (err: any) {
      toast({ title: err.message || 'Error', variant: 'destructive' });
    }
  };

  const handleRemove = async (id: number) => {
    try {
      await api.delete(`/team/members/${id}`);
      fetchData();
    } catch (err: any) {
      toast({ title: err.message || 'Error', variant: 'destructive' });
    }
  };

  if (!enabled) {
    return <UpgradePrompt feature="team_invites" />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-primary rounded-full border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold mb-1">{t('team_title')}</h3>
        <p className="text-sm text-slate-500">{t('team_desc')}</p>
      </div>

      {/* Invite Form */}
      <div className="flex items-center gap-3">
        <div
          className="flex-1 flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-sm min-h-[40px] cursor-text focus-within:ring-2 focus-within:ring-ring focus-within:ring-inset"
          onClick={() => document.getElementById('email-input')?.focus()}
        >
          {emails.map((e) => (
            <Badge key={e} variant="secondary" className="gap-1 pr-1">
              {e}
              <button
                type="button"
                className="ml-0.5 rounded-full hover:bg-slate-300 p-0.5"
                onClick={(ev) => { ev.stopPropagation(); removeEmail(e); }}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <input
            id="email-input"
            className="flex-1 min-w-[180px] h-8 bg-transparent outline-none placeholder:text-muted-foreground"
            placeholder={emails.length === 0 ? (t('invite_email_placeholder') || 'Enter email address') : ''}
            value={inputValue}
            onChange={(e) => {
              const val = e.target.value;
              if (val.includes(',')) {
                val.split(',').forEach((part) => addEmail(part));
              } else {
                setInputValue(val);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addEmail(inputValue);
              }
              if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
                removeEmail(emails[emails.length - 1]);
              }
            }}
            onBlur={() => { if (inputValue.trim()) addEmail(inputValue); }}
            disabled={isAtLimit('team_members') || atEmailLimit}
          />
        </div>
        <Button onClick={handleInvite} disabled={inviting || emails.length === 0 || isAtLimit('team_members')}>
          <UserPlus className="h-4 w-4 mr-1.5" />
          {t('send_invitation')}
        </Button>
      </div>

      {isAtLimit('team_members') && (
        <UpgradePrompt feature="team_members" />
      )}

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-slate-600 mb-2">{t('pending_invitations')}</h4>
          <div className="rounded-lg border divide-y">
            {invitations.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-slate-400" />
                  <div>
                    <div className="text-sm font-medium">{inv.email}</div>
                    <div className="text-xs text-slate-400">
                      {t('invited_by')} {inv.inviter.first_name} {inv.inviter.last_name}
                    </div>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleRevoke(inv.id)}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Members */}
      <div>
        <h4 className="text-sm font-semibold text-slate-600 mb-2">{t('team_members')}</h4>
        <div className="rounded-lg border divide-y">
          {members.map((member) => (
            <div key={member.id} className="flex items-center justify-between px-4 py-3">
              <div>
                <div className="text-sm font-medium">
                  {member.first_name} {member.last_name}
                  {member.is_customer_admin && (
                    <span className="ml-2 text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">{t('admin')}</span>
                  )}
                </div>
                <div className="text-xs text-slate-400">{member.email}</div>
              </div>
              {!member.is_customer_admin && (
                <Button variant="ghost" size="sm" onClick={() => handleRemove(member.id)}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Team;
